import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function sortByRole(a: { role: string }, b: { role: string }) {
  const roles = ["SUPERADMIN", "ADMIN", "MEMBER", "ACCOUNTANT", "DRIVER"];
  return roles.indexOf(a.role) - roles.indexOf(b.role);
}

export const arraysEqual = (a: any[], b: any[]): boolean => {
  if (a.length !== b.length) return false;

  // Create a copy of b to avoid mutating the original
  const bCopy = [...b];

  // For each item in a, find and remove a matching item from bCopy
  for (const item of a) {
    const matchingIndex = bCopy.findIndex(
      (bItem) =>
        Object.keys(item).every((key) => item[key] === bItem[key]) &&
        Object.keys(bItem).every((key) => item[key] === bItem[key]),
    );

    if (matchingIndex === -1) return false;
    bCopy.splice(matchingIndex, 1);
  }

  return true;
};

export const stripDiacritics = (value: string): string =>
  value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

// Ligatures and stroked letters are single codepoints, so NFD cannot decompose
// them into a base letter plus a combining mark.
const NON_DECOMPOSABLE_LETTERS: Record<string, string> = {
  æ: "ae",
  Æ: "AE",
  œ: "oe",
  Œ: "OE",
  ø: "o",
  Ø: "O",
  ß: "ss",
  ẞ: "SS",
  đ: "d",
  Đ: "D",
  ð: "d",
  Ð: "D",
  ł: "l",
  Ł: "L",
  þ: "th",
  Þ: "TH",
  ı: "i",
};

const NON_DECOMPOSABLE_PATTERN = new RegExp(
  `[${Object.keys(NON_DECOMPOSABLE_LETTERS).join("")}]`,
  "g",
);

export const accentsTidy = (value: string): string =>
  stripDiacritics(value).replace(
    NON_DECOMPOSABLE_PATTERN,
    (letter) => NON_DECOMPOSABLE_LETTERS[letter],
  );

const SAFE_CHARACTERS = /[^0-9a-zA-Z!_\\.\\*'\\(\\)\\\-/]/g;
export const sanitizeKey = (value: string) =>
  value.replace(SAFE_CHARACTERS, " ").replace(/\s+/g, "-");

/**
 * Day bounds, always on a copy. `date.setHours(...)` mutates its receiver, so
 * calling it on a Date held in state silently rewrites that state — use these.
 */
export function toStartOfDay(date: Date): Date {
  const d = new Date(date.getTime());
  d.setHours(0, 0, 0, 0);
  return d;
}

export function toEndOfDay(date: Date): Date {
  const d = new Date(date.getTime());
  d.setHours(23, 59, 59, 999);
  return d;
}

export const toStartOfDayIso = (date: Date): string => toStartOfDay(date).toISOString();

export const toEndOfDayIso = (date: Date): string => toEndOfDay(date).toISOString();
